import { zodResolver } from '@hookform/resolvers/zod';
import { useFeedback } from '@ibe/ui';
import { useForm, type DefaultValues, type FieldValues, type UseFormReturn } from 'react-hook-form';
import { type ZodType } from 'zod';

import { messageForError } from '../api/error-message.js';
import { ApiError } from '../api/errors.js';
import { logger } from '../logger/logger.js';
import { applyServerErrors } from './server-errors.js';

/**
 * Every form in the portal.
 *
 * A module supplies a schema and a submit function; this supplies the six
 * things each of the other forms would otherwise re-implement:
 *
 * 1. Schema validation, through the same Zod schema the module's API layer uses.
 * 2. **Server field errors attached to their fields**, via `applyServerErrors`.
 * 3. **Everything else in the feedback dialog** — and never both, because a
 *    message shown twice reads as two problems. A dialog rather than a toast
 *    because the outcome of a deliberate action is a result to acknowledge,
 *    not an announcement to miss; and because a failure the user does not see
 *    means they believe a change was saved when it was not.
 * 4. A success confirmation, carrying the correlation id when one exists.
 * 5. **No double submit.** react-hook-form's `isSubmitting` gates it, so a
 *    double-clicked create button is one request.
 * 6. Unexpected failures logged, with the correlation id when there is one.
 */

export interface AppFormOptions<TValues extends FieldValues> {
  readonly schema: ZodType<TValues, TValues>;
  readonly onSubmit: (values: TValues) => Promise<void>;
  readonly defaultValues?: Partial<TValues>;
  /** Announced on success. Omit for a form whose result is self-evident. */
  readonly successMessage?: string;
  /** Runs after a successful submit — close a dialog, navigate. */
  readonly onSuccess?: () => void;
}

export interface AppForm<TValues extends FieldValues> extends UseFormReturn<TValues> {
  /** Wire straight to `<form onSubmit={…}>`. */
  readonly submit: (event?: React.BaseSyntheticEvent) => Promise<void>;
  readonly submitting: boolean;
}

export function useAppForm<TValues extends FieldValues>(
  options: AppFormOptions<TValues>,
): AppForm<TValues> {
  const { showSuccess, showError } = useFeedback();

  const form = useForm<TValues>({
    resolver: zodResolver(options.schema),
    ...(options.defaultValues === undefined
      ? {}
      : { defaultValues: options.defaultValues as DefaultValues<TValues> }),
  });

  const submit = async (event?: React.BaseSyntheticEvent): Promise<void> => {
    // A double-clicked create button is still one request. handleSubmit is not
    // itself re-entrant, so this guard is what makes the button's "wait" real.
    if (form.formState.isSubmitting) return;
    await form.handleSubmit(async (values) => {
      try {
        await options.onSubmit(values);

        if (options.successMessage !== undefined) {
          showSuccess(options.successMessage);
        }
        options.onSuccess?.();
      } catch (error) {
        // Field errors first. If they landed, the message is already beside the
        // input that caused it and a toast would repeat it.
        const handled = applyServerErrors(
          error,
          (field, fieldError) => {
            form.setError(field as Parameters<typeof form.setError>[0], fieldError);
          },
          Object.keys(form.getValues()),
        );

        if (handled) return;

        logger.error('form submission failed', {
          message: error instanceof Error ? error.message : String(error),
        });
        // The reference is only offered when the platform produced one. A
        // proxy or load balancer that answered instead of a service carries
        // none, and an empty "Reference" line is worse than no line.
        showError(
          messageForError(error),
          undefined,
          error instanceof ApiError && error.correlationId !== '' ? error.correlationId : undefined,
        );
      }
    })(event);
  };

  return {
    ...form,
    submit,
    // Surfaced explicitly so a caller disables its submit button from one
    // value rather than reaching into `formState` for it.
    submitting: form.formState.isSubmitting,
  };
}
